// ============================================================
// VidyaTrack — Top Navigation Bar
// ============================================================

import React, { useState, useRef, useEffect } from "react";
import { FiMenu, FiSearch, FiSun, FiMoon, FiChevronDown,
         FiLogOut, FiUser, FiSettings, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth }  from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import NotificationCenter from "../notifications/NotificationCenter";
import { toast } from "react-toastify";

const ROLE_COLOR = {
  admin:"#054bb4", teacher:"#059669", student:"#7c3aed", parent:"#d97706"
};

export default function TopBar({ onMenuToggle }) {
  const { userData, logout } = useAuth();
  const { dark, toggle }     = useTheme();
  const navigate             = useNavigate();

  const [showProfile, setShowProfile] = useState(false);
  const [search,      setSearch]      = useState("");
  const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);
  const profileRef = useRef();

  // Track mobile
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Close profile on outside click
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = e => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfile(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleLogout = async () => {
    setShowProfile(false);
    try { await logout(); navigate("/login"); toast.success("Signed out successfully"); }
    catch { toast.error("Sign out failed"); }
  };

  const initials  = (userData?.name || "VT").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const roleColor = ROLE_COLOR[userData?.role] || "#054bb4";
  const firstName = userData?.name?.split(" ")[0] || "User";

  // Colours
  const bg      = dark ? "#1a2535" : "#ffffff";
  const border  = dark ? "#2d3748" : "#e5e7eb";
  const btnBg   = dark ? "#1e293b" : "#f3f4f6";
  const btnHov  = dark ? "#1e3a6e" : "#e8eef8";
  const textClr = dark ? "#e2e8f0" : "#374151";

  return (
    <header style={{
      height:         60,
      background:     bg,
      borderBottom:   `1px solid ${border}`,
      display:        "flex",
      alignItems:     "center",
      padding:        "0 16px",
      gap:            10,
      position:       "sticky",
      top:            0,
      zIndex:         100,
      flexShrink:     0,
      boxShadow:      "0 1px 0 " + border,
    }}>

      {/* ── Hamburger (always rendered, visible on mobile) ── */}
      <button
        onClick={onMenuToggle}
        aria-label="Toggle menu"
        style={{
          width:56, height:40,
          background:  "none",
          border:      "none",
          cursor:      "pointer",
          color:       textClr,
          borderRadius: 8,
          display:     "flex",
          alignItems:  "center",
          justifyContent:"center",
          flexShrink:  0,
          // Show on mobile, hide on desktop via inline style
          // Using isMobile state so it's controlled by JS (avoids CSS specificity fights)
          opacity: isMobile ? 1 : 0,
          pointerEvents: isMobile ? "auto" : "none",
          transition: "opacity .2s",
        }}
      >
        <FiMenu size={22}/>
      </button>

      {/* ── Search ── */}
      <div style={{
        display:    "flex",
        alignItems: "center",
        gap:        8,
        padding:    "7px 12px",
        background: dark ? "#111827" : "#f9fafb",
        border:     `1.5px solid ${border}`,
        borderRadius: 8,
        flex:       1,
        maxWidth:   isMobile ? "100%" : 360,
        minWidth:   0,
        transition: "border-color .15s",
      }}>
        <FiSearch size={14} style={{ color: dark ? "#64748b" : "#9ca3af", flexShrink:0 }}/>
        <input
          type="text"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border:"none", background:"none", outline:"none", fontSize:".875rem", flex:1, color: dark ? "#e2e8f0" : "#374151", fontFamily:"inherit", minWidth:0 }}
        />
        {search && (
          <button onClick={() => setSearch("")} style={{ background:"none", border:"none", cursor:"pointer", color: dark?"#64748b":"#9ca3af", display:"flex", alignItems:"center", flexShrink:0 }}>
            <FiX size={13}/>
          </button>
        )}
      </div>

      <div style={{ flex:1 }}/>

      {/* ── Dark mode toggle ── */}
      <button
        onClick={toggle}
        title={dark ? "Switch to light mode" : "Switch to dark mode"}
        style={{ width:36, height:36, background:btnBg, border:"none", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color: dark?"#93c5fd":"#6b7280", transition:"background .15s", flexShrink:0 }}
        onMouseEnter={e => e.currentTarget.style.background = btnHov}
        onMouseLeave={e => e.currentTarget.style.background = btnBg}
      >
        {dark ? <FiSun size={17}/> : <FiMoon size={17}/>}
      </button>

      {/* ── Notifications ── */}
      <NotificationCenter/>

      {/* ── Profile dropdown ── */}
      <div ref={profileRef} style={{ position:"relative", flexShrink:0 }}>
        <button
          onClick={() => setShowProfile(o => !o)}
          style={{
            display:"flex", alignItems:"center", gap:7,
            background: showProfile ? btnHov : btnBg,
            border:    `1px solid ${border}`,
            cursor:    "pointer",
            padding:   "5px 10px 5px 5px",
            borderRadius: 10,
            transition: "background .15s",
            flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.background = btnHov}
          onMouseLeave={e => { if (!showProfile) e.currentTarget.style.background = btnBg; }}
        >
          <div style={{ width:28, height:28, borderRadius:7, background:roleColor, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:".7rem", fontWeight:800, flexShrink:0 }}>
            {initials}
          </div>
          {/* Hide name on small phones */}
          {!isMobile && (
            <span style={{ fontSize:".82rem", fontWeight:600, color:textClr, maxWidth:90, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {firstName}
            </span>
          )}
          <FiChevronDown size={12} style={{ color: dark?"#64748b":"#9ca3af", transform: showProfile?"rotate(180deg)":"none", transition:"transform .15s", flexShrink:0 }}/>
        </button>

        {showProfile && (
          <div style={{
            position:     "absolute",
            top:          "calc(100% + 8px)",
            right:        0,
            width:        210,
            background:   bg,
            border:       `1px solid ${border}`,
            borderRadius: 14,
            boxShadow:    "0 16px 48px rgba(0,0,0,0.16)",
            zIndex:       200,
            animation:    "fadeIn .15s ease",
            overflow:     "hidden",
          }}>
            <div style={{ padding:"12px 14px", background: dark?"#0f2040":"#f4f7fd", borderBottom:`1px solid ${border}` }}>
              <div style={{ fontWeight:700, fontSize:".86rem", color: dark?"#f1f5f9":"#111827" }}>{userData?.name}</div>
              <div style={{ fontSize:".72rem", color: dark?"#94a3b8":"#6b7280", marginTop:2 }}>{userData?.email}</div>
              <div style={{ marginTop:5, display:"inline-block", background:roleColor, color:"#fff", fontSize:".62rem", fontWeight:700, padding:"1px 8px", borderRadius:20, textTransform:"uppercase" }}>
                {userData?.role}
              </div>
            </div>

            {[
              { icon:FiUser,     label:"My Profile", action:() => { navigate("/profile"); setShowProfile(false); } },
              { icon:FiSettings, label:"Settings",   action:() => { navigate("/profile"); setShowProfile(false); } },
            ].map(item => (
              <button key={item.label} onClick={item.action} style={{
                width:"100%", display:"flex", alignItems:"center", gap:9,
                padding:"10px 14px", background:"none", border:"none",
                cursor:"pointer", color: dark?"#cbd5e1":"#374151",
                fontSize:".83rem", fontFamily:"inherit", fontWeight:500,
                transition:"background .12s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = dark?"#1e293b":"#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <item.icon size={14} style={{ color: dark?"#64748b":"#9ca3af" }}/> {item.label}
              </button>
            ))}

            <div style={{ borderTop:`1px solid ${border}` }}>
              <button onClick={handleLogout} style={{
                width:"100%", display:"flex", alignItems:"center", gap:9,
                padding:"10px 14px", background:"none", border:"none",
                cursor:"pointer", color:"#ef4444",
                fontSize:".83rem", fontFamily:"inherit", fontWeight:600,
                transition:"background .12s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = dark?"#3d1515":"#fef2f2"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <FiLogOut size={14}/> Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
